import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Container,
    Group,
    Loader,
    Stack,
    Switch,
    Text,
    Title,
    Tooltip,
    Badge as Pill,
} from '@mantine/core';
import { IconPencil, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useEffect, useState, type FC } from 'react';

import {
    deleteReportSchedule,
    describeScheduleFilters,
    isFiltersEmpty,
    listReportSchedules,
    updateReportSchedule,
    type ReportBodyBlock,
    type ReportSchedule,
} from '@api/reportSchedules';
import { useCurrentUser } from '@hooks/useCurrentUser';

import ScheduleReportModal from './ScheduleReportModal';
import { computeNextRunIst, formatCadence, formatNextRun } from './scheduleTiming';
import classes from './ScheduledEmails.module.css';

function describeBodyBlock(block: ReportBodyBlock): string {
    switch (block.type) {
        case 'TEXT':
            return new DOMParser().parseFromString(block.html, 'text/html').body.textContent?.trim() || 'Empty text';
        case 'TABLE_IMAGE': {
            const rows = block.capture?.rowLimit;
            const image = rows ? `Table image, first ${rows} rows` : 'Table image';
            return block.caption ? `${image}: ${block.caption}` : image;
        }
        case 'AI_SUMMARY':
            return 'AI summary';
    }
}

/** An attachment's stored label ends in the row count on the day it was set up, not at send. */
const attachmentName = (label: string): string => label.replace(/ — \d+ rows?$/, '');

const nextRunOf = (schedule: ReportSchedule): Date | null =>
    computeNextRunIst(
        schedule.frequency,
        schedule.triggerTime,
        schedule.triggerDaysOfWeek,
        schedule.triggerDayOfMonth ?? undefined,
    );

const Detail: FC<{ label: string; lines: string[] }> = ({ label, lines }) => (
    <>
        <Text size="xs" c="dimmed">
            {label}
        </Text>
        <div className={classes.detailValue}>
            {lines.map((line, index) => (
                <Text key={index} size="xs">
                    {line}
                </Text>
            ))}
        </div>
    </>
);

function formatRunStatus(schedule: ReportSchedule): {
    label: string;
    color: string;
} {
    if (!schedule.lastRunAt) {
        return { label: 'Not run yet', color: 'gray' };
    }
    const when = new Date(schedule.lastRunAt).toLocaleString();
    if (schedule.lastRunStatus === 'SUCCESS') {
        return { label: `Last sent ${when}`, color: 'green' };
    }
    if (schedule.lastRunStatus === 'FAILED') {
        return { label: `Failed ${when}`, color: 'red' };
    }
    return { label: when, color: 'gray' };
}

const ScheduledEmailsContainer: FC = () => {
    const { currentUser } = useCurrentUser();
    const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<ReportSchedule | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await listReportSchedules();
            setSchedules(result);
        } catch (e: any) {
            setError(
                e?.error?.message ?? e?.message ?? 'Failed to load schedules',
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleToggle = async (schedule: ReportSchedule, enabled: boolean) => {
        try {
            const updated = await updateReportSchedule(schedule.id, {
                enabled,
            });
            setSchedules((curr) =>
                curr.map((s) => (s.id === schedule.id ? updated : s)),
            );
        } catch (e: any) {
            setError(
                e?.error?.message ?? e?.message ?? 'Failed to update schedule',
            );
        }
    };

    const handleDelete = async (schedule: ReportSchedule) => {
        if (!confirm(`Delete "${schedule.name}"?`)) return;
        try {
            await deleteReportSchedule(schedule.id);
            setSchedules((curr) => curr.filter((s) => s.id !== schedule.id));
        } catch (e: any) {
            setError(
                e?.error?.message ?? e?.message ?? 'Failed to delete schedule',
            );
        }
    };

    return (
        <Container size="md" py="xl">
            <Group justify="space-between" mb="md">
                <Title order={2}>Scheduled emails</Title>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={load}
                    aria-label="Refresh"
                >
                    <IconRefresh size={18} />
                </ActionIcon>
            </Group>

            <Text c="dimmed" size="sm" mb="lg">
                Reports and tables emailed on a recurring schedule. Create new
                ones from the email icon on any table.
            </Text>

            {loading && (
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            )}

            {error && (
                <Card withBorder p="md" mb="md" bg="red.0">
                    <Text c="red">{error}</Text>
                </Card>
            )}

            {!loading && !error && schedules.length === 0 && (
                <Card withBorder p="xl">
                    <Stack align="center" gap="xs">
                        <Text fw={500}>No scheduled emails yet</Text>
                        <Text size="sm" c="dimmed">
                            Click the email icon on any table to schedule one.
                        </Text>
                    </Stack>
                </Card>
            )}

            {editing && (
                <ScheduleReportModal
                    key={editing.id}
                    opened={!!editing}
                    onClose={() => setEditing(null)}
                    defaultName={editing.name}
                    builtinReportType={editing.builtinReportType ?? undefined}
                    currentUserEmail={currentUser?.email ?? ''}
                    existing={editing}
                    onUpdated={(updated) => {
                        setSchedules((curr) =>
                            curr.map((s) =>
                                s.id === updated.id ? updated : s,
                            ),
                        );
                        setEditing(null);
                    }}
                />
            )}

            <Stack gap="sm">
                {schedules.map((schedule) => {
                    const status = formatRunStatus(schedule);
                    const nextRun = schedule.enabled ? nextRunOf(schedule) : null;
                    const body = schedule.body ?? [];
                    const attachments = schedule.attachments ?? [];
                    return (
                        <Card key={schedule.id} withBorder p="md">
                            <Group
                                justify="space-between"
                                wrap="nowrap"
                                align="flex-start"
                            >
                                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                    <Group gap="xs">
                                        <Text fw={600}>{schedule.name}</Text>
                                        <Badge
                                            color={
                                                schedule.sourceKind === 'ALERT'
                                                    ? 'grape'
                                                    : 'indigo'
                                            }
                                            variant="light"
                                            size="sm"
                                        >
                                            {schedule.sourceKind === 'ALERT'
                                                ? 'Alert'
                                                : 'Report'}
                                        </Badge>
                                        {!schedule.enabled && (
                                            <Badge
                                                color="gray"
                                                variant="light"
                                                size="sm"
                                            >
                                                Paused
                                            </Badge>
                                        )}
                                    </Group>
                                    <div className={classes.details}>
                                        <Detail
                                            label="Schedule"
                                            lines={[
                                                formatCadence(schedule),
                                                ...(nextRun ? [`Next: ${formatNextRun(nextRun)}`] : []),
                                            ]}
                                        />
                                        <Detail label="To" lines={schedule.recipients} />
                                        <Detail
                                            label="Subject"
                                            lines={[schedule.subject || 'Reparo: {{name}} — {{date}} (default)']}
                                        />
                                        <Detail
                                            label="Body"
                                            lines={
                                                body.length
                                                    ? body.map(describeBodyBlock)
                                                    : ['Default: summary line and attachment list']
                                            }
                                        />
                                        <Detail
                                            label="Attachments"
                                            lines={
                                                attachments.length
                                                    ? attachments.map((a) => attachmentName(a.label))
                                                    : ['One file from the report filters']
                                            }
                                        />
                                    </div>
                                    {!isFiltersEmpty(schedule.filters) && (
                                        <Group gap={4} mt={2}>
                                            {describeScheduleFilters(
                                                schedule.filters,
                                            ).map((chip) => (
                                                <Pill
                                                    key={chip.key}
                                                    color="indigo"
                                                    variant="light"
                                                    size="sm"
                                                >
                                                    {chip.label}
                                                </Pill>
                                            ))}
                                        </Group>
                                    )}
                                    <Group gap="xs" mt={4}>
                                        <Badge
                                            color={status.color}
                                            variant="light"
                                            size="sm"
                                        >
                                            {status.label}
                                        </Badge>
                                        {schedule.lastRunError && (
                                            <Tooltip
                                                label={schedule.lastRunError}
                                                multiline
                                                w={300}
                                            >
                                                <Badge
                                                    color="red"
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    Error
                                                </Badge>
                                            </Tooltip>
                                        )}
                                    </Group>
                                </Stack>
                                <Group gap="xs" wrap="nowrap">
                                    <Switch
                                        checked={schedule.enabled}
                                        color="indigo"
                                        onChange={(e) =>
                                            handleToggle(
                                                schedule,
                                                e.currentTarget.checked,
                                            )
                                        }
                                        aria-label="Enabled"
                                    />
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        onClick={() => setEditing(schedule)}
                                        aria-label="Edit"
                                    >
                                        <IconPencil size={18} />
                                    </ActionIcon>
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        onClick={() => handleDelete(schedule)}
                                        aria-label="Delete"
                                    >
                                        <IconTrash size={18} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        </Card>
                    );
                })}
            </Stack>
        </Container>
    );
};

export default ScheduledEmailsContainer;
